
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { View, StripeProduct } from '../types';
import { infoContent } from '../data/infoContent';

// --- ICONS ---
const CheckIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4 text-green-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>;
const GoogleIcon: React.FC = () => (<svg className="w-5 h-5" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg"><g><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path><path fill="none" d="M0 0h48v48H0z"></path></g></svg>);
const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>;
const DrRxLogoIcon = () => <svg width="40" height="40" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="24" cy="24" r="5" fill="#FACC15" className="logo-glow"/><line x1="24" y1="9" x2="24" y2="17" stroke="white" strokeWidth="2"/><circle cx="24" cy="7" r="2" fill="white"/><line x1="24" y1="31" x2="24" y2="39" stroke="white" strokeWidth="2"/><circle cx="24" cy="41" r="2" fill="white"/><line x1="9" y1="24" x2="17" y2="24" stroke="white" strokeWidth="2"/><circle cx="7" cy="24" r="2" fill="white"/><line x1="31" y1="24" x2="39" y2="24" stroke="white" strokeWidth="2"/><circle cx="41" cy="24" r="2" fill="white"/></svg>;

// --- CONFIGURATION ---
const PLAN_CONFIG: Record<string, { 
    label: string, 
    color: string, 
    badge?: string, 
    icon: string,
    highlightFeatures: string[] 
}> = {
    'Free': {
        label: 'Explorer',
        color: 'border-blue-900/50 bg-gradient-to-br from-[#1a2a47] to-[#15243E]',
        icon: '🌱',
        highlightFeatures: ['10 AI Chats/Day', 'Drug Index', 'Basic Tools']
    },
    'Plus': {
        label: 'Learner',
        color: 'border-indigo-500/50 bg-gradient-to-br from-[#232845] to-[#15243E] shadow-[0_0_15px_rgba(99,102,241,0.1)]',
        badge: 'BEST VALUE',
        icon: '📚',
        highlightFeatures: ['Unlimited AI', 'Study Mode', 'Advanced Tools']
    },
    'Pro': {
        label: 'Clinician',
        color: 'border-yellow-400 bg-gradient-to-br from-[#2A2F45] to-[#15243E] shadow-[0_0_20px_rgba(250,204,21,0.15)]',
        badge: 'POPULAR',
        icon: '🩺',
        highlightFeatures: ['Evidence AI', 'Complex Calcs', 'Priority Support']
    },
    'Teams': {
        label: 'Institution',
        color: 'border-emerald-500/50 bg-gradient-to-br from-[#1a2a47] to-[#15243E]',
        icon: '🏥',
        highlightFeatures: ['Multi-User', 'API Access', 'Admin Dashboard']
    }
};

interface PricingProps {
    onNavigate: (view: View) => void;
    onOpenInfoModal: (key: keyof typeof infoContent) => void;
}

const Pricing: React.FC<PricingProps> = ({ onNavigate, onOpenInfoModal }) => {
    const { user, subscription, products, productsLoading, redirectToCheckout } = useAuth();
    const [isRedirecting, setIsRedirecting] = useState<string | null>(null);
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

    const handleSelectPlan = (plan: StripeProduct) => {
        // Select correct price based on toggle
        const targetInterval = billingCycle === 'yearly' ? 'year' : 'month';
        const priceObj = plan.prices?.find(p => p.interval === targetInterval) || plan.prices?.[0];
        const priceId = priceObj?.id;

        if (plan.role === 'Free') {
            onNavigate('dashboard');
            return;
        }
        
        if (priceId) {
            setIsRedirecting(priceId);
            redirectToCheckout(priceId);
        } else {
            alert('Pricing information is currently unavailable. Please try again later.');
        }
    };

    if (productsLoading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center text-white space-y-6 bg-[#0F172A]">
                <div className="relative">
                    <div className="absolute inset-0 animate-ping rounded-full bg-yellow-400/20"></div>
                    <DrRxLogoIcon />
                </div>
                <div className="flex flex-col items-center gap-2">
                    <div className="w-6 h-6 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-lg font-semibold text-yellow-100 animate-pulse">
                        Loading plans...
                    </p>
                </div>
            </div>
        );
    }

    // Ensure plans are sorted: Free, Plus, Pro, Teams
    const sortedProducts = [...products].sort((a, b) => {
        const order = ['Free', 'Plus', 'Pro', 'Teams'];
        return order.indexOf(a.role) - order.indexOf(b.role);
    });

    return (
        <>
            {/* Use 100dvh for mobile browser address bar handling */}
            <div className="fixed inset-0 bg-[#0F172A] text-white flex flex-col h-[100dvh] overflow-hidden page-enter-active">
                
                {/* --- TOP HEADER (Flex Shrink) --- */}
                <div className="flex-shrink-0 pt-6 pb-2 px-4 text-center relative z-10 bg-[#0F172A]">
                    <div className="flex flex-col md:flex-row justify-center items-center gap-4 mb-4">
                        <h1 className="text-2xl md:text-4xl font-extrabold tracking-tight">
                            Choose Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 to-amber-500">Power</span>
                        </h1>
                         {/* Billing Toggle */}
                        <div className="inline-flex bg-black/40 p-1 rounded-xl border border-blue-900/50 backdrop-blur-md ml-0 md:ml-4">
                            <button 
                                onClick={() => setBillingCycle('monthly')}
                                className={`px-4 py-2 rounded-lg text-xs md:text-sm font-bold transition-all ${billingCycle === 'monthly' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
                            >
                                Monthly
                            </button>
                            <button 
                                onClick={() => setBillingCycle('yearly')}
                                className={`px-4 py-2 rounded-lg text-xs md:text-sm font-bold transition-all relative ${billingCycle === 'yearly' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
                            >
                                Yearly
                                <span className="absolute -top-2.5 -right-2 bg-green-500 text-white text-[9px] md:text-[10px] px-2 py-0.5 rounded-full font-black shadow-sm animate-bounce">SAVE 20%</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* --- SCROLLABLE GRID (Flex Grow) --- */}
                {/* Updated padding: md:pt-24 to prevent desktop clipping, pb-32 for mobile footer clearance */}
                <div className="flex-grow w-full overflow-y-auto custom-scrollbar px-2 sm:px-6 py-2 md:pt-24 pb-32">
                     <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-6 lg:gap-8 max-w-screen-xl mx-auto content-start lg:content-center">
                        
                        {sortedProducts.map((plan) => {
                            const config = PLAN_CONFIG[plan.role] || PLAN_CONFIG['Free'];
                            
                            // Price Logic
                            const monthlyPriceObj = plan.prices?.find(p => p.interval === 'month');
                            const yearlyPriceObj = plan.prices?.find(p => p.interval === 'year');
                            
                            let displayPrice = '0';
                            let crossedOutPrice = '';
                            let subtext = '/mo';
                            let billedText = '';

                            if (plan.role !== 'Free' && plan.role !== 'Teams') {
                                if (billingCycle === 'yearly' && yearlyPriceObj) {
                                    // Calculate effective monthly price from yearly total
                                    displayPrice = ((yearlyPriceObj.unit_amount / 100) / 12).toFixed(2);
                                    // Get the normal monthly price to show strikethrough
                                    if (monthlyPriceObj) {
                                        crossedOutPrice = (monthlyPriceObj.unit_amount / 100).toFixed(2);
                                    }
                                    billedText = `$${(yearlyPriceObj.unit_amount / 100).toFixed(2)} billed yearly`;
                                } else if (monthlyPriceObj) {
                                    displayPrice = (monthlyPriceObj.unit_amount / 100).toFixed(2);
                                    billedText = 'billed monthly';
                                }
                            } else if (plan.role === 'Free') {
                                billedText = 'forever free';
                            }
                            
                            const isCurrentPlan = plan.role?.toLowerCase() === subscription?.plan?.toLowerCase();
                            const isPopular = plan.popular;
                            
                            return (
                                <div 
                                    key={plan.id}
                                    className={`
                                        relative flex flex-col 
                                        rounded-xl p-3 sm:p-6 lg:p-8
                                        border transition-all duration-300 
                                        h-auto min-h-[350px] md:min-h-[450px]
                                        ${config.color}
                                        ${isPopular ? 'z-10 border-yellow-400/60 bg-[#1a2a47] transform md:-translate-y-2 shadow-2xl' : 'border-opacity-50 hover:border-blue-500/50 hover:bg-[#1a2a47]/80'}
                                    `}
                                >
                                    {/* Animated Badge */}
                                    {config.badge && (
                                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-max">
                                            <div className="relative">
                                                <div className="absolute inset-0 bg-yellow-400 blur-md opacity-40 animate-pulse rounded-full"></div>
                                                <div className="relative bg-gradient-to-r from-yellow-300 to-amber-500 text-gray-900 text-[9px] sm:text-xs font-black px-3 py-1 rounded-full uppercase tracking-widest shadow-xl border border-yellow-200/50">
                                                    {config.badge}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Content */}
                                    <div className="flex flex-col h-full">
                                        <div className="mb-1 sm:mb-3 text-center mt-2">
                                            <div className="text-xl sm:text-3xl md:text-4xl mb-1">{config.icon}</div>
                                            <h3 className="text-sm sm:text-xl md:text-2xl font-bold text-white truncate">{plan.name}</h3>
                                            <p className="text-[9px] sm:text-xs md:text-sm font-bold text-blue-300 uppercase tracking-wider opacity-80">{config.label}</p>
                                        </div>

                                        <div className="text-center mb-2 sm:mb-5 md:mb-8 bg-black/20 rounded-lg py-2">
                                            {plan.role === 'Teams' ? (
                                                <span className="text-lg sm:text-3xl md:text-4xl font-bold text-white">Custom</span>
                                            ) : (
                                                <div>
                                                     {/* Strikethrough Price for Yearly Deal */}
                                                    {crossedOutPrice && (
                                                        <div className="text-[10px] sm:text-xs text-gray-400 line-through decoration-red-400 decoration-2 font-semibold mb-0.5">
                                                            ${crossedOutPrice}
                                                        </div>
                                                    )}
                                                    <div className="flex items-baseline justify-center">
                                                        <span className="text-xs md:text-base align-top mr-0.5">$</span>
                                                        <span className="text-2xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight">{plan.role === 'Free' ? '0' : displayPrice}</span>
                                                        <span className="text-gray-400 ml-0.5 font-medium text-[10px] sm:text-sm md:text-base">{subtext}</span>
                                                    </div>
                                                    {billedText && <p className="text-[8px] sm:text-[10px] md:text-xs text-gray-500 font-medium uppercase tracking-wide mt-1">{billedText}</p>}
                                                </div>
                                            )}
                                        </div>

                                        <div className="h-px bg-white/10 mb-3 md:mb-6"></div>

                                        <ul className="space-y-1.5 sm:space-y-3 md:space-y-4 flex-grow mb-2">
                                            {config.highlightFeatures.map((feat, i) => (
                                                <li key={i} className="flex items-start gap-2">
                                                    <div className="mt-0.5 text-green-400 flex-shrink-0"><CheckIcon /></div>
                                                    <span className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-300 text-left leading-snug">{feat}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        <div className="mt-auto pt-2 md:pt-4">
                                            {plan.role === 'Teams' ? (
                                                <a 
                                                    href="https://www.nvpharmaceutics.com/clinical-ai-solutions" 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="block w-full py-2 md:py-3 rounded-xl bg-blue-600/20 border border-blue-500 text-blue-300 font-bold text-center text-xs md:text-sm hover:bg-blue-600 hover:text-white transition-all"
                                                >
                                                    Contact Sales
                                                </a>
                                            ) : (
                                                <button
                                                    onClick={() => handleSelectPlan(plan)}
                                                    disabled={isCurrentPlan || (isRedirecting === plan.id)}
                                                    className={`w-full py-2 md:py-3 rounded-xl font-bold text-xs md:text-sm tracking-wide transition-all shadow-md ${
                                                        isCurrentPlan 
                                                        ? 'bg-gray-700/50 text-gray-400 cursor-default border border-gray-600' 
                                                        : isPopular 
                                                        ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-gray-900 hover:shadow-yellow-500/40 hover:-translate-y-0.5' 
                                                        : 'bg-white text-gray-900 hover:bg-gray-100 hover:-translate-y-0.5'
                                                    }`}
                                                >
                                                    {isCurrentPlan ? 'Current Plan' : isRedirecting ? 'Processing...' : 'Select Plan'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                     </div>
                </div>

                {/* --- FOOTER LINKS (Flex Shrink, Not Absolute) --- */}
                <div className="flex-shrink-0 pb-6 pt-3 text-center bg-[#0F172A] border-t border-blue-900/30 z-20 w-full pb-[env(safe-area-inset-bottom,24px)]">
                    <button onClick={() => onNavigate('dashboard')} className="text-gray-500 hover:text-white text-xs md:text-sm font-medium transition-colors px-4 py-2">
                        Back to Dashboard
                    </button>
                </div>
            </div>
        </>
    );
};

export default Pricing;
